// File: src/components/Invoices/OperationalLifecycle.jsx

import React from "react";
import {
  ShoppingCart,
  Truck,
  PackageCheck,
  FileText,
  ScanLine,
  Link2,
  ClipboardCheck,
  CreditCard,
  CheckCircle,
  Clock,
  AlertTriangle,
  XCircle,
} from "lucide-react";

function statusClass(state) {
  const safe = String(state || "").toLowerCase();

  if (safe === "completed") {
    return {
      icon: "border-green-400/30 bg-green-500/10 text-green-300",
      text: "text-green-300",
      line: "bg-green-400/30",
    };
  }

  if (safe === "current") {
    return {
      icon: "border-amber-400/30 bg-amber-500/10 text-amber-300",
      text: "text-amber-300",
      line: "bg-amber-400/30",
    };
  }

  if (safe === "blocked" || safe === "failed" || safe === "rejected") {
    return {
      icon: "border-red-400/30 bg-red-500/10 text-red-300",
      text: "text-red-300",
      line: "bg-red-400/30",
    };
  }

  return {
    icon: "border-white/10 bg-white/5 text-gray-400",
    text: "text-gray-400",
    line: "bg-white/10",
  };
}

function buildLifecycle(invoice = {}) {
  const status = String(invoice?.status || invoice?.payment_status || "").toLowerCase();

  const hasPo = Boolean(invoice?.po_id || invoice?.po_number);
  const uploaded = Boolean(invoice?.uploaded_at || invoice?.created_at);
  const approved = Boolean(invoice?.approved_at || status === "approved" || status === "paid");
  const rejected = Boolean(invoice?.rejected_at || status === "rejected");
  const paid = Boolean(invoice?.paid_at || status === "paid");

  const paidOrApproved = paid || approved;

  return [
    {
      key: "po",
      label: "Purchase Order",
      description: hasPo ? "Invoice is linked to a PO." : "No linked PO found yet.",
      icon: ShoppingCart,
      state: hasPo ? "completed" : "waiting",
    },
    {
      key: "goods",
      label: "Goods Receiving",
      description: hasPo
        ? "Receiving activity can be checked against the linked PO."
        : "Receiving cannot be verified without a PO.",
      icon: PackageCheck,
      state: hasPo ? "completed" : "waiting",
    },
    {
      key: "invoice",
      label: "Invoice Created",
      description: uploaded ? "Invoice exists in the system." : "Invoice creation date not found.",
      icon: FileText,
      state: uploaded ? "completed" : "waiting",
    },
    {
      key: "ocr",
      label: "OCR / Extraction",
      description: invoice?.extraction_status
        ? `Extraction status: ${invoice.extraction_status}`
        : "Extraction data not available on this record yet.",
      icon: ScanLine,
      state: invoice?.extraction_status ? "completed" : "waiting",
    },
    {
      key: "match",
      label: "PO Match",
      description: hasPo ? "Invoice can be matched against the linked PO." : "Waiting for PO link.",
      icon: Link2,
      state: hasPo ? "completed" : "waiting",
    },
    {
      key: "approval",
      label: "Finance Approval",
      description: rejected
        ? "Invoice was rejected."
        : approved
          ? "Invoice has been approved."
          : "Invoice is awaiting finance approval.",
      icon: rejected ? XCircle : ClipboardCheck,
      state: rejected ? "rejected" : approved ? "completed" : uploaded ? "current" : "waiting",
    },
    {
      key: "payment",
      label: "Payment",
      description: paid
        ? "Invoice has been fully paid."
        : approved
          ? "Invoice is approved and ready for payment."
          : "Payment is waiting for approval.",
      icon: CreditCard,
      state: paid ? "completed" : approved ? "current" : "waiting",
    },
    {
      key: "closed",
      label: "Completed",
      description: paid
        ? "Invoice lifecycle is complete."
        : paidOrApproved
          ? "Invoice is not fully closed yet."
          : "Invoice is still in progress.",
      icon: CheckCircle,
      state: paid ? "completed" : "waiting",
    },
  ];
}

export default function OperationalLifecycle({ invoice }) {
  const steps = buildLifecycle(invoice);

  const blockedStep = steps.find((step) =>
    ["blocked", "failed", "rejected"].includes(step.state)
  );

  const currentStep = steps.find((step) => step.state === "current");

  return (
    <div className="rounded-2xl border border-white/10 bg-[#101010] p-5">
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold">Operational Lifecycle</h2>
          <p className="mt-1 text-sm text-gray-400">
            End-to-end invoice journey from PO link to payment completion.
          </p>
        </div>

        <div className="flex gap-2 flex-wrap">
          {blockedStep ? (
            <span className="inline-flex items-center gap-2 rounded-full border border-red-400/30 bg-red-500/10 px-3 py-1 text-xs font-semibold text-red-300">
              <AlertTriangle className="h-4 w-4" />
              Blocked at {blockedStep.label}
            </span>
          ) : currentStep ? (
            <span className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-500/10 px-3 py-1 text-xs font-semibold text-amber-300">
              <Clock className="h-4 w-4" />
              Current: {currentStep.label}
            </span>
          ) : (
            <span className="inline-flex items-center gap-2 rounded-full border border-green-400/30 bg-green-500/10 px-3 py-1 text-xs font-semibold text-green-300">
              <CheckCircle className="h-4 w-4" />
              Complete
            </span>
          )}
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const styles = statusClass(step.state);

          return (
            <div
              key={step.key}
              className="relative rounded-xl border border-white/10 bg-black/25 p-4"
            >
              <div className="flex items-start gap-3">
                <div
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border ${styles.icon}`}
                >
                  <Icon className="h-4 w-4" />
                </div>

                <div>
                  <div className={`text-sm font-semibold ${styles.text}`}>
                    {step.label}
                  </div>
                  <div className="mt-1 text-xs text-gray-400">
                    {step.description}
                  </div>
                </div>
              </div>

              <div className="mt-3 text-[11px] uppercase tracking-wide text-gray-500">
                Step {index + 1} · {step.state}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}